/**
 * Group Approvals HTTP client.
 *
 * Day 14 §5.3.2 Group Approvals.
 */
import api from "./api";
import type {
  ApprovalQueueResponse,
  ApprovalQueueFilters,
  ExpenditureRecord,
  ThresholdResponse,
} from "@/types/group-approvals.types";

function unwrap<T>(res: { data: unknown }): T {
  const body = res.data as { success?: boolean; data?: T } | T;
  if (
    body &&
    typeof body === "object" &&
    "data" in (body as Record<string, unknown>) &&
    "success" in (body as Record<string, unknown>)
  ) {
    return (body as { data: T }).data;
  }
  return body as T;
}

export const groupApprovalsService = {
  async getQueue(
    filters: ApprovalQueueFilters = {},
  ): Promise<ApprovalQueueResponse> {
    const params: Record<string, string> = {};
    if (filters.businessId) params.businessId = filters.businessId;
    if (filters.escalatedOnly) params.escalatedOnly = "true";
    const res = await api.get("/group/approvals", { params });
    return unwrap<ApprovalQueueResponse>(res);
  },

  async approve(expenditureId: string): Promise<ExpenditureRecord> {
    const res = await api.post(`/group/approvals/${expenditureId}/approve`);
    return unwrap<ExpenditureRecord>(res);
  },

  async reject(
    expenditureId: string,
    reason: string,
  ): Promise<ExpenditureRecord> {
    const res = await api.post(`/group/approvals/${expenditureId}/reject`, {
      reason,
    });
    return unwrap<ExpenditureRecord>(res);
  },

  async getThreshold(): Promise<ThresholdResponse> {
    const res = await api.get("/group/approvals/threshold");
    return unwrap<ThresholdResponse>(res);
  },

  async setThreshold(threshold: number): Promise<ThresholdResponse> {
    const res = await api.put("/group/approvals/threshold", { threshold });
    return unwrap<ThresholdResponse>(res);
  },
};
